import {
  CameraAlt,
  RemoveRounded,
  Link as LinkIcon,
  FiberManualRecord,
  AddCommentOutlined,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createFilterOptions,
  FormControl,
  TextField,
  Tooltip,
  Autocomplete,
  Button,
} from "@mui/material";
import { setSeverityIndex } from "app/store/transmission/slice/transmissionSlice";
import NoteComments from "components/pages/transmissions/modal/detail/notes/report_notes/note_comments/NoteComments";

import {
  CLINIC_ACTIVE,
  CLINIC_FOR_REVIEW,
  CLINIC_ROLES,
  REPORT_SEVERITY,
  REPORT_SEVERITY_MAP,
} from "static/constants";

const filter = createFilterOptions();

const ReportNoteInput = (props) => {
  const {
    note,
    errors,
    noteIndex,
    reportNote,
    arrayHelpers,
    setFieldValue,
    reportNoteIndex,
    navigateToPdfPage,
    currentPageNumber,
    isReportNoteEditable,
  } = props;

  const {
    isFetchingCoverPage,
    coverPageLength,
    reportDetail,
    hasReportNoteEditAccess,
    reportCurrentTab,
  } = useSelector((state) => state.transmission);

  const { currentUser } = useSelector((state) => state.auth);
  const isClinicActiveOrReviewTab = [CLINIC_ACTIVE, CLINIC_FOR_REVIEW].includes(
    reportCurrentTab
  );

  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState("");
  const [showNoteCommentForm, setShowNoteCommentForm] = useState(false);

  const notePageNumber =
    isFetchingCoverPage && coverPageLength
      ? Number(note.pageNumber) + Number(coverPageLength)
      : note.pageNumber;

  useEffect(() => {
    note &&
      dispatch(
        setSeverityIndex(note?.alert ? REPORT_SEVERITY.indexOf(note.alert) : 0)
      );

    return () => {
      dispatch(setSeverityIndex(0));
    };
  }, [dispatch, note]);

  const handleSeverityChange = (noteAlert) => {
    const newSeverityIndex =
      (REPORT_SEVERITY.indexOf(noteAlert) + 1) % REPORT_SEVERITY.length;
    dispatch(setSeverityIndex(newSeverityIndex));

    setFieldValue(
      `reportNotes[${reportNoteIndex}].content[${noteIndex}].alert`,
      REPORT_SEVERITY[newSeverityIndex]
    );
  };

  const handleCameraClick = () => {
    const linkedNote = {
      ...note,
      pageNumber: note.pageNumber ? null : currentPageNumber,
    };

    setFieldValue(
      `reportNotes[${reportNoteIndex}].content[${noteIndex}]`,
      linkedNote
    );
  };

  const onPageChange = () => {
    note.pageNumber && navigateToPdfPage(notePageNumber);
  };

  return isReportNoteEditable ? (
    <div key={noteIndex} className="report-note-input">
      <FormControl fullWidth variant="outlined" margin="none" size="small">
        <Autocomplete
          freeSolo
          id={`reportNotes[${reportNoteIndex}].content[${noteIndex}].description`}
          name={`reportNotes[${reportNoteIndex}].content[${noteIndex}].description`}
          openOnFocus
          clearOnBlur
          value={note?.description || ""}
          options={reportNote?.placeholders?.filter(Boolean) || []}
          getOptionLabel={(option) => option}
          onInputChange={(_event, newValue, _reason) => {
            const value = newValue || "";

            setInputValue(value);
            setFieldValue(
              `reportNotes[${reportNoteIndex}].content[${noteIndex}].description`,
              value
            );
          }}
          onChange={(event, newValue) => {
            setFieldValue(
              `reportNotes[${reportNoteIndex}].content[${noteIndex}].description`,
              newValue
            );
          }}
          onBlur={() => {
            setFieldValue(
              `reportNotes[${reportNoteIndex}].content[${noteIndex}].description`,
              note?.description?.trim() || ""
            );
          }}
          error={
            !!errors?.reportNotes?.[reportNoteIndex]?.content?.[noteIndex]
              ?.description
          }
          helperText={
            errors?.reportNotes?.[reportNoteIndex]?.content?.[noteIndex]
              ?.description
          }
          filterOptions={(options, params) => {
            const filtered = filter(options, { ...params, inputValue });

            return filtered;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              multiline
              maxRows={3}
              size="small"
              id="reportNote"
              variant="outlined"
              className="reportNote"
              inputProps={{
                ...params?.inputProps,
                spellCheck: true,
              }}
              sx={{
                ".MuiAutocomplete-input": {
                  padding: "2.5px 6px 2.5px 8px !important",
                  whiteSpace: "break-spaces",
                },
                ".MuiAutocomplete-endAdornment": {
                  right: "2px !important",
                },
              }}
            />
          )}
        />
      </FormControl>

      <div className="d-flex flex-column">
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip
            title={
              note.pageNumber
                ? `Unlink page: ${note.pageNumber}`
                : `Link page: ${currentPageNumber || "None"}`
            }
          >
            <CameraAlt
              onClick={handleCameraClick}
              style={{
                color: note.pageNumber ? "#94ba33" : "#949494",
                marginLeft: "10px",
                cursor: "pointer",
                fontSize: "24px",
              }}
            />
          </Tooltip>

          <Tooltip title={`Severity: ${note.alert}`}>
            <FiberManualRecord
              onClick={() => handleSeverityChange(note.alert)}
              style={{
                color: REPORT_SEVERITY_MAP[note.alert],
                cursor: "pointer",
                fontSize: "26px",
              }}
              value={note.alert}
            />
          </Tooltip>

          <Tooltip title="Remove note">
            <RemoveRounded
              onClick={() => arrayHelpers.remove(noteIndex)}
              style={{ cursor: "pointer", fontSize: "30px" }}
            />
          </Tooltip>
        </div>
        {note.pageNumber && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Tooltip title={`Go To page ${note.pageNumber}`}>
              <LinkIcon
                onClick={onPageChange}
                className="report-note-editable-hyperlink"
                style={{
                  marginLeft: "10px",
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  ) : (
    <>
      <div
        key={noteIndex}
        className="report-note-input--read-only"
        style={{
          color: "#ffffff",
          padding: "6px",
          backgroundColor: REPORT_SEVERITY_MAP[note.alert],
        }}
      >
        {note.pageNumber ? (
          <Tooltip
            title={`Navigate to page ${notePageNumber}`}
            placement="left-start"
          >
            <div
              className="report-note-input__description-read-only"
              onClick={onPageChange}
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              {note.description}
            </div>
          </Tooltip>
        ) : (
          <div
            className="report-note-input__description-read-only"
            onClick={onPageChange}
            style={{ textDecoration: "none", cursor: "default" }}
          >
            {note.description}
          </div>
        )}

        {CLINIC_ROLES.includes(currentUser?.role) &&
          !hasReportNoteEditAccess &&
          !note?.comments?.length &&
          !showNoteCommentForm &&
          isClinicActiveOrReviewTab &&
          note?._id && (
            <Tooltip title="Add comment">
              <Button
                onClick={() => setShowNoteCommentForm((prev) => !prev)}
                sx={(theme) => ({
                  minWidth: "auto",
                  padding: "0px !important",
                  "&:hover": {
                    color: theme.palette.secondary.main,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "4px",
                  },
                })}
              >
                <AddCommentOutlined
                  fontSize="small"
                  sx={{
                    transform: "scaleX(-1)",
                    "&:hover": {
                      padding: "2px",
                    },
                  }}
                />
              </Button>
            </Tooltip>
          )}
      </div>

      {(showNoteCommentForm || note?.comments?.length > 0) && (
        <NoteComments
          contentId={note?._id}
          reportId={reportDetail?._id}
          comments={note?.comments || []}
          showCommentForm={showNoteCommentForm}
          setShowNoteCommentForm={setShowNoteCommentForm}
          isClinicActiveOrReviewTab={isClinicActiveOrReviewTab}
        />
      )}
    </>
  );
};

export default ReportNoteInput;
